import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState, useEffect, useReducer } from 'react';

import Modal from './Modal';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';

import { MUTATION, QUERY } from '../../gql-operations';

import styled from 'styled-components';
import { IoMdClose } from 'react-icons/io';
import { FiFolder, FiSearch, FiPlus } from 'react-icons/fi';

import SelectableList from '../SelectableList';

import { AuthContext } from '../../util/context';

import useGetSubfolders from '../../hooks/useGetSubfolders';
import useUpdateFoldersOnTree from '../../hooks/useUpdateFoldersOnTree';
import useAddFoldersToTree from '../../hooks/useAddFoldersToTree';
import useMoveMessagesMutation from '../../hooks/useMoveMessagesMutation';
import useUpdateGetFolderContent from '../../hooks/useUpdateGetFolderContent';
import loadMoreFilteredFolders from '../Folder/Components/loadMoreFilteredFolders';
import { WHITE, PALE_WHITE_6 } from '../../appearance/Colors';

import useRedirectErrors from '../../errors/useRedirectErrors';

import customAnalytics from '../../util/custom-analytics';
import { HISTORY_SORT_FIELD, DEFAULT_SORT_FIELD, DateField } from '../../constants/dateField';
import { STACK_SEARCH_PRESETS, createStackFolder } from '../../constants/stackPresets';
import { MESSAGE_TYPE_PRESETS, createTypeFolder } from '../../constants/typePresets';

export const searchActions = {
    JUMP: 'JUMP',
    MOVE_FOLDER: 'MOVE_FOLDER',
    MOVE_MESSAGES: 'MOVE_MESSAGES'
};

const normalizePresetSearchValue = value =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ');

const getPresetSearchScore = (preset, searchText) => {
    const normalizedSearchText = normalizePresetSearchValue(searchText);
    if (!normalizedSearchText) {
        return null;
    }

    const searchableTerms = [
        preset.label,
        preset.id,
        ...(preset.keywords || [])
    ].map(normalizePresetSearchValue);

    let bestScore = null;
    searchableTerms.forEach(term => {
        if (!term) return;

        let score = null;
        if (term === normalizedSearchText) {
            score = 0;
        } else if (term.startsWith(normalizedSearchText)) {
            score = 1;
        } else if (normalizedSearchText.length > 1 && term.includes(normalizedSearchText)) {
            score = 2;
        }

        if (score !== null && (bestScore === null || score < bestScore)) {
            bestScore = score;
        }
    });

    return bestScore;
};

const getMatchingVirtualFolders = searchText => [
    ...STACK_SEARCH_PRESETS
        .map(preset => {
            const score = getPresetSearchScore(preset, searchText);
            if (score === null) {
                return null;
            }

            return {
                ...createStackFolder(preset),
                path: 'Stack',
                _ResultIcon: preset.Icon,
                _ResultIconColor: preset.iconColor,
                _virtualSearchScore: score,
                _virtualSearchKindOrder: 0
            };
        }),
    ...MESSAGE_TYPE_PRESETS
        .map(preset => {
            const score = getPresetSearchScore(preset, searchText);
            if (score === null) {
                return null;
            }

            return {
                ...createTypeFolder(preset),
                path: 'Type',
                _ResultIcon: preset.Icon,
                _ResultIconColor: preset.iconColor,
                _virtualSearchScore: score,
                _virtualSearchKindOrder: 1
            };
        })
]
        .filter(Boolean)
        .sort((left, right) => (
            left._virtualSearchScore - right._virtualSearchScore ||
            left._virtualSearchKindOrder - right._virtualSearchKindOrder ||
            left.title.localeCompare(right.title)
        ))
        .slice(0, 10);

function SearchFoldersModal({
    isOpen,
    closeModal,
    currentFolder,
    addNew,
    title,
    addNewMoveMessage,
    enableMoveToRoot,
    dropdownMessage,
    disableMoveToInbox,
    action,
    suggestedFolderTitle,
    messagesToSendOverride = null,
    selectedMessageOverride = null,
    onClearSelectionOverride = null
}) {
    const {t: lnT} = useTranslation();

    const client = useApolloClient();
    const [folderSearchInput, setFolderSearchInput] = useState('');
    // const [ignored, forceUpdate] = useReducer(x => x + 1, 0);
    const [createFolderMutation, { data, loading, error }] = useMutation(MUTATION.CREATE_FOLDER, {
        onCompleted: res => {
            customAnalytics.create_folder_from_move_message();
        }
    });
    const [moveMessages] = useMoveMessagesMutation();
    const { moveFolderToRoot, createAndMoveFolder } = useUpdateFoldersOnTree();
    const [addFoldersToTree] = useAddFoldersToTree();
    const { updateNewFolders } = useUpdateGetFolderContent();
    const [loadMoreCheck, setLoadMoreCheck] = useState(true);
    const [loadingOlder, setLoadingOlder] = useState(false);

    const [getSubfolders] = useGetSubfolders();

    const { passingErrors } = useRedirectErrors();

    const inputRef = React.useRef();

    const {
        state: {
            selectedFolder,
            messagesToSend: globalMessagesToSend,
            selectedMessage: globalSelectedMessage,
            homeFolder
        },
        context: { setSelectedFolder, setMessagesToSend, setSelectedMessagesMap, setShowMultiSelect }
    } = React.useContext(AuthContext);
    const messagesToSend = Array.isArray(messagesToSendOverride) ? messagesToSendOverride : globalMessagesToSend;
    const selectedMessage = selectedMessageOverride || globalSelectedMessage;

    const [findFolders, { data: findFolderData, error: findFolderError, loading: findFolderLoading, fetchMore }] =
        useLazyQuery(QUERY.FIND_FOLDERS, {
            onError: res => {
                passingErrors(res);
            },
            fetchPolicy: 'network-only'
        });

    useEffect(() => {
        // handleFolderSearchChange('');
        if (isOpen) {
            customAnalytics.modalview('Search Folders');
            customAnalytics.pageview('/message', 'SearchFoldersScreen');
            findFolders({
                variables: {
                    data: {
                        text: '',
                        sortBy: getSortField('', action)
                    }
                }
            });
        }
        return () => {
            // cleanup
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const suggested = (suggestedFolderTitle || '').trim();
        if (!suggested) return;

        setFolderSearchInput(suggested);
        findFolders({
            variables: {
                data: {
                    text: suggested,
                    sortBy: getSortField(suggested, action)
                }
            }
        });
    }, [isOpen, suggestedFolderTitle, action]);

    const afterOpenModal = () => {
        // references are now sync'd and can be accessed.
        // this.subtitle.style.color = '#f00';
    };

    // Add this helper function near the top of your component (inside SearchFoldersModal)
    const getSortField = (searchText, action) => {
        if (searchText.trim() === '') {
            // When no search text, decide based on the current search action
            if (action === searchActions.JUMP) {
                return HISTORY_SORT_FIELD; // Use the HISTORY_SORT_FIELD constant for JUMP
            } else {
                // For MOVE_FOLDER or MOVE_MESSAGES, use the alternate sort field
                return DateField.LAST_MOVED_AT;
            }
        } else {
            // When search text is not empty fall back to the default sort field
            return DEFAULT_SORT_FIELD;
        }
    };

    const handleFolderSearchChange = value => {
        setFolderSearchInput(value);
        // console.log('here is going to be commenced search');
        findFolders({
            variables: {
                data: {
                    text: value,
                    sortBy: getSortField(value, action)
                }
            }
        });
        // putting back the load after each search
        if (!loadMoreCheck) {
            setLoadMoreCheck(true);
        }
    };

    const resetFoldersSearch = () => {
        handleFolderSearchChange('');
        setFolderSearchInput('');
    };

    const moveToRoot = async () => {
        if (currentFolder) {
            let updatedFolder = await moveFolderToRoot(currentFolder);
            console.log('updated', updatedFolder);
            closeModal();
            setTimeout(() => {
                if (updatedFolder) {
                    if (document.getElementById(updatedFolder.id)) {
                        document.getElementById(updatedFolder.id).scrollIntoView();
                    }
                }
            }, 150);
        } else if (selectedFolder) {
            let updatedFolder = await moveFolderToRoot(selectedFolder);
            console.log('updated', updatedFolder);
            closeModal();
            setTimeout(() => {
                if (updatedFolder) {
                    if (document.getElementById(updatedFolder.id)) {
                        document.getElementById(updatedFolder.id).scrollIntoView();
                    }
                }
            }, 150);
        }
    };

    const moveToInbox = async () => {
        if (homeFolder) {
            moveMessagesTo(homeFolder);
            closeModal(homeFolder);
        }
    };

    const renderMoveToRoot = () => {
        if (enableMoveToRoot) {
            if (currentFolder && currentFolder.parent) {
                // console.log('this is the current', currentFolder)
                return <RootFolder type="button" onClick={() => moveToRoot()}><FiFolder size={13} />{lnT('folders:moveToRoot_9cb6fba6')}</RootFolder>;
            } else if (selectedFolder && selectedFolder.parent) {
                return <RootFolder type="button" onClick={() => moveToRoot()}><FiFolder size={13} />{lnT('folders:moveToRoot_9cb6fba6')}</RootFolder>;
            }
        }
    };

    const renderMoveToInbox = () => {
        if (!enableMoveToRoot && !disableMoveToInbox) {
            if (homeFolder && messagesToSend && messagesToSend.length > 0) {
                if ((selectedFolder && selectedFolder.id.toString()) !== homeFolder.id.toString()) {
                    return <RootFolder type="button" onClick={() => moveToInbox()}><FiFolder size={13} />{lnT('folders:moveToInbox')}</RootFolder>;
                }
            }
            // NO inbox move for folders
            else if (homeFolder && dropdownMessage) {
                const dropdownFolderId = String(dropdownMessage?.folder?.id || selectedFolder?.id || '');
                const homeFolderId = String(homeFolder?.id || '');
                const isThreadChildMessage = Boolean(dropdownMessage?.parentMessageId);
                if (
                    (dropdownFolderId && dropdownFolderId !== homeFolderId) ||
                    (isThreadChildMessage && dropdownFolderId === homeFolderId)
                ) {
                    return <RootFolder type="button" onClick={() => moveToInbox()}><FiFolder size={13} />{lnT('folders:moveToInbox')}</RootFolder>;
                }
            } else if (homeFolder && selectedMessage) {
                const selectedMessageFolderId = String(selectedMessage?.folder?.id || selectedFolder?.id || '');
                const homeFolderId = String(homeFolder?.id || '');
                const isThreadChildMessage = Boolean(selectedMessage?.parentMessageId);
                if (
                    (selectedMessageFolderId && selectedMessageFolderId !== homeFolderId) ||
                    (isThreadChildMessage && selectedMessageFolderId === homeFolderId)
                ) {
                    return <RootFolder type="button" onClick={() => moveToInbox()}><FiFolder size={13} />{lnT('folders:moveToInbox')}</RootFolder>;
                }
            }
        }
    };

    const removeSelectedMessages = () => {
        if (typeof onClearSelectionOverride === 'function') {
            onClearSelectionOverride();
            return;
        }
        setSelectedMessagesMap({});
        setMessagesToSend([]);
        setShowMultiSelect(false);
    };

    const checkHomeFolder = folder => {
        if (homeFolder.id === folder.id) {
            return null;
        } else {
            return folder.id;
        }
    };

    const registerCreatedFolder = async (createdFolder, parentFolder) => {
        if (!createdFolder?.id) {
            return;
        }

        // Creating from "Move to…" bypasses the normal folder-create flow,
        // so neither the sidebar tree nor the opened parent cache used to be
        // updated until a full reload.
        await addFoldersToTree([createdFolder]);
        if (parentFolder?.id && createdFolder.parent) {
            updateNewFolders(parentFolder, [createdFolder]);
        } else if (!createdFolder.parent) {
            // Root folders are rendered from the getHomeFolders-backed tree.
            // Keep that cache entry in sync so later query notifications do
            // not rebuild TreeFolder without this just-created folder.
            client.cache.modify({
                fields: {
                    getHomeFolders(existing = {}, { readField, toReference }) {
                        const existingFolders = existing.folders || [];
                        const alreadyPresent = existingFolders.some(folder =>
                            readField('id', folder) === createdFolder.id
                        );

                        if (alreadyPresent) {
                            return existing;
                        }

                        return {
                            ...existing,
                            folders: [toReference(createdFolder), ...existingFolders]
                        };
                    }
                }
            });
        }
    };

    const createFolder = async () => {
        // console.log('messages and message', messagesToSend, selectedMessage);
        if (dropdownMessage) {
            let response = await createFolderMutation({
                variables: {
                    data: {
                        title: folderSearchInput,
                        parent: checkHomeFolder(dropdownMessage.folder)
                    }
                },
            });

            let justCreated = await response.data.createFolder;

            if (justCreated) {
                await registerCreatedFolder(justCreated, dropdownMessage.folder);
                await moveMessages([dropdownMessage], justCreated, dropdownMessage.folder);
                closeModal(justCreated);
                resetFoldersSearch();
                removeSelectedMessages();
                // route.params.callback(true);
                // analytics here because we can follow the 
                customAnalytics.message_edit({
                    "action": "Move",
                    "type": "Just created folder",
                    'multi_select': messagesToSend && messagesToSend.length,
                    "from": dropdownMessage && dropdownMessage.folder.title === "Inbox" ? "Inbox" : dropdownMessage && dropdownMessage.folder.parent ? "Subfolder" : "HomeFolder",
                    "destination": justCreated.parent ? "Subfolder" : "HomeFolder"
                }, dropdownMessage)
            }
        } else if (messagesToSend.length > 0) {
            let response = await createFolderMutation({
                variables: {
                    data: {
                        title: folderSearchInput,
                        parent: checkHomeFolder(messagesToSend[0].folder)
                    }
                },
            });

            let justCreated = await response.data.createFolder;

            if (justCreated) {
                await registerCreatedFolder(justCreated, messagesToSend[0].folder);
                await moveMessages(messagesToSend, justCreated, messagesToSend[0].folder);
                closeModal(justCreated);
                resetFoldersSearch();
                removeSelectedMessages();
                // route.params.callback(true);
                // analytics here because we can follow the 
                messagesToSend.forEach(message => {
                    customAnalytics.message_edit({
                        "action": "Move",
                        "type": "Just created folder",
                        'multi_select': messagesToSend && messagesToSend.length,
                        "from": message && message.folder.title === "Inbox" ? "Inbox" : message && message.folder.parent ? "Subfolder" : "HomeFolder",
                        "destination": justCreated.parent ? "Subfolder" : "HomeFolder"
                    }, message)
                });
            }
        } else {
            let response = await createFolderMutation({
                variables: {
                    data: {
                        title: folderSearchInput,
                        parent: checkHomeFolder(selectedMessage.folder)
                    }
                }
            });

            let justCreated = await response.data.createFolder;

            if (justCreated) {
                await registerCreatedFolder(justCreated, selectedMessage.folder);
                await moveMessages([selectedMessage], justCreated, selectedMessage.folder);
                closeModal(justCreated);
                resetFoldersSearch();
                removeSelectedMessages();
                // route.params.callback(true);
                // analytics here because we can follow the 
                customAnalytics.message_edit({
                    "action": "Move",
                    "type": "Just created folder",
                    'multi_select': messagesToSend && messagesToSend.length,
                    "destination": selectedMessage && selectedMessage.folder.title === "Inbox" ? "Inbox" : selectedMessage && selectedMessage.folder.parent ? "Subfolder" : "HomeFolder",
                    "from": justCreated.parent ? "Subfolder" : "HomeFolder"
                }, selectedMessage)
            }
        }
        customAnalytics.create_folder_from_move_message();
    };

    const renderAddNewMoveMessage = () => {
        if (addNewMoveMessage && folderSearchInput.length > 0) {
            return (
                <SearchFolder
                    type="button"
                    onClick={() => {
                        createFolder();
                        // let response = await createFolder({
                        //     variables: {
                        //         data: {
                        //             title: folderSearchInput,
                        //             parent: selectedFolder.id
                        //         }
                        //     }
                        // });
                        // let createdFolder = response.data.createFolder;
                        // closeModal(createdFolder);
                        // resetFoldersSearch();
                        // setSelectedFolder(createdFolder);
                        // setTimeout(() => {
                        //     if (createdFolder) {
                        //         if (document.getElementById(createdFolder.id)) {
                        //             document.getElementById(createdFolder.id).scrollIntoView();
                        //         }
                        //     }
                        // }, 150);
                    }}
                >
                    <FiPlus size={15} />
                    <span>{lnT('folders:create_e52488af')}{folderSearchInput}”</span>
                </SearchFolder>
            );
        }
    };

    const renderAddNew = () => {
        if (addNew && folderSearchInput.length > 0) {
            return (
                <SearchFolder
                    type="button"
                    onClick={async () => {
                        console.log('you run this guy, dont you');
                        if (currentFolder) {
                            let createdFolder = await createAndMoveFolder(folderSearchInput, currentFolder);
                            closeModal();
                            resetFoldersSearch();
                            setTimeout(() => {
                                if (createdFolder) {
                                    if (document.getElementById(createdFolder.id)) {
                                        document.getElementById(createdFolder.id).scrollIntoView();
                                    }
                                }
                            }, 150);
                        } else if (selectedFolder) {
                            let createdFolder = await createAndMoveFolder(folderSearchInput, selectedFolder);
                            closeModal();
                            resetFoldersSearch();
                            setTimeout(() => {
                                if (createdFolder) {
                                    if (document.getElementById(createdFolder.id)) {
                                        document.getElementById(createdFolder.id).scrollIntoView();
                                    }
                                }
                            }, 150);
                        }
                    }}
                >
                    <FiPlus size={15} />
                    <span>{lnT('folders:create_e52488af')}{folderSearchInput}”</span>
                </SearchFolder>
            );
        }
    };

    // if (findFolderLoading) return null;

    if (findFolderData) {
        // console.log('all of them', findFolderData);
    }

    const showFoldersList = () => {
        const virtualFolders = action === searchActions.JUMP
            ? getMatchingVirtualFolders(folderSearchInput)
            : [];

        if (!findFolderData?.findFolders?.folders) {
            return virtualFolders;
        }

        const folders = findFolderData.findFolders.folders;
        let sortedFolders = [...folders];

        // For move actions, sort by LAST_MOVED_AT locally
        if (action === searchActions.MOVE_FOLDER || action === searchActions.MOVE_MESSAGES) {
            sortedFolders.sort((a, b) => {
                // If both have LAST_MOVED_AT, compare them
                if (a[DateField.LAST_MOVED_AT] && b[DateField.LAST_MOVED_AT]) {
                    return new Date(b[DateField.LAST_MOVED_AT]) - new Date(a[DateField.LAST_MOVED_AT]);
                }
                // If only one has LAST_MOVED_AT, it should come first
                if (a[DateField.LAST_MOVED_AT]) return -1;
                if (b[DateField.LAST_MOVED_AT]) return 1;
                // If neither has LAST_MOVED_AT, maintain server's sort order
                return 0;
            });
        }

        // If we have a suggested folder title, ensure it's at the very top
        if (suggestedFolderTitle) {
            const index = sortedFolders.findIndex(f => f.title.toLowerCase() === suggestedFolderTitle.toLowerCase());
            if (index !== -1) {
                const suggested = sortedFolders.splice(index, 1)[0];
                // Clone to avoid mutating Apollo query result objects
                sortedFolders.unshift({
                    ...suggested,
                    _isAiSuggestion: true
                });
            }
        }

        if (action === searchActions.MOVE_FOLDER) {
            const currentFolderId = currentFolder ? currentFolder.id : selectedFolder?.id;
            if (currentFolderId) {
                sortedFolders = sortedFolders.filter(folder => folder.id !== currentFolderId);
            }
        }

        if (virtualFolders.length === 0) {
            return sortedFolders;
        }

        const folderIds = new Set(sortedFolders.map(folder => String(folder.id)));
        const uniqueVirtualFolders = virtualFolders.filter(folder => !folderIds.has(String(folder.id)));

        return [...uniqueVirtualFolders, ...sortedFolders];
    };

    const onCloseModal = () => {
        closeModal(undefined);
        // cleanup
        resetFoldersSearch();
    };

    const moveMessagesTo = toFolder => {
        if (!toFolder) {
            return;
        }

        // When an AI folder suggestion was shown, record whether the user took it
        if ((suggestedFolderTitle || '').trim()) {
            customAnalytics.ai_suggestion({
                'type': 'folder',
                'action': toFolder._isAiSuggestion ? 'Accepted' : 'Rejected'
            });
        }

        if (dropdownMessage) {
            const sourceFolder = dropdownMessage.folder || selectedFolder || homeFolder || toFolder;
            moveMessages([dropdownMessage], toFolder, sourceFolder);
            customAnalytics.message_edit({
                "action": "Move",
                "destination": toFolder && toFolder.title === "Inbox" ? "Inbox" : toFolder && toFolder.parent ? "Subfolder" : "HomeFolder",
                "from": sourceFolder?.title === "Inbox" ? "Inbox" : sourceFolder?.parent ? "Subfolder" : "HomeFolder"
            }, dropdownMessage);
        } else if (messagesToSend.length > 0) {
            const messagesByFolder = messagesToSend.reduce((map, message) => {
                const folderId = message?.folder?.id || selectedFolder?.id;
                if (!folderId) {
                    return map;
                }
                if (!map.has(folderId)) {
                    map.set(folderId, {
                        folder: message.folder || selectedFolder,
                        messages: []
                    });
                }
                map.get(folderId).messages.push(message);
                return map;
            }, new Map());

            messagesByFolder.forEach(({ folder: fromFolder, messages }) => {
                moveMessages(messages, toFolder, fromFolder || selectedFolder);
                messages.forEach(message => {
                    customAnalytics.message_edit({
                        "action": "Move",
                        'multi_select': messagesToSend.length,
                        "destination": toFolder && toFolder.title === "Inbox" ? "Inbox" : toFolder && toFolder.parent ? "Subfolder" : "HomeFolder",
                        "from": fromFolder?.title === "Inbox" ? "Inbox" : fromFolder?.parent ? "Subfolder" : "HomeFolder"
                    }, message);
                });
            });
        } else if (selectedMessage) {
            const sourceFolder = selectedMessage.folder || selectedFolder;
            moveMessages([selectedMessage], toFolder, sourceFolder);
            customAnalytics.message_edit({
                "action": "Move",
                "destination": toFolder && toFolder.title === "Inbox" ? "Inbox" : toFolder && toFolder.parent ? "Subfolder" : "HomeFolder",
                "from": sourceFolder?.title === "Inbox" ? "Inbox" : sourceFolder?.parent ? "Subfolder" : "HomeFolder"
            }, selectedMessage);
        }
        removeSelectedMessages();
        // console.log('moveMessagesTo', toFolder, selectedFolder)
        // console.log('messagesToSend', messagesToSend, messagesToSend && messagesToSend.length)
    };

    const renderFooter = () => {
        if (loadMoreCheck) {
            if (findFolderData && findFolderData.findFolders.folders.length >= 10) {
                return (
                    <div
                        style={{
                            // justifyContent: 'center',
                            alignItems: 'center',
                            marginVertical: 16,
                            color: PALE_WHITE_6
                        }}
                    >{lnT('common:loading')}</div>
                );
            }
        }
    };

    const loadContent = () => {
        const folderItems = showFoldersList();

        if (findFolderLoading && folderItems.length === 0) {
            return <LoadingText>{lnT('common:loading')}</LoadingText>;
        } else {
            return (
                <>
                    {renderAddNew()}
                    {renderAddNewMoveMessage()}
                    <SelectableList
                        currentItem={currentFolder ? currentFolder : selectedFolder}
                        items={folderItems}
                        selectedItem={value => {
                            resetFoldersSearch();
                            if (action === searchActions.JUMP) {
                                closeModal(value);
                            } else if (action === searchActions.MOVE_FOLDER) {
                                // function to move message to selected folder
                                closeModal(value);
                            } else {
                                moveMessagesTo(value);
                                closeModal(value);
                            }
                        }}
                        loadMore={loadMore}
                        variant="folderCards"
                    ></SelectableList>
                    {renderFooter()}
                </>
            );
        }
    };

    const loadMore = () => {
        if (!findFolderData || !findFolderData.findFolders) {
            console.error('No folder data available for loading more');
            setLoadMoreCheck(false);
            return;
        }

        const originalFolders = findFolderData.findFolders.folders;
        if (!originalFolders || !Array.isArray(originalFolders)) {
            console.error('Invalid folders data structure');
            setLoadMoreCheck(false);
            return;
        }

        loadMoreFilteredFolders(
            folderSearchInput,
            originalFolders,
            fetchMore,
            {
                loadingMore: loadingOlder,
                cursor: findFolderData.findFolders.cursor,
                setLoadingOlder,
                setLoadMoreCheck
            },
            folderSearchInput.length === 0 ? HISTORY_SORT_FIELD : DEFAULT_SORT_FIELD
        );
    };

    if (findFolderData && findFolderData.folders) {
        console.log('folderssss', findFolderData.findFolders.folders);
    }

    return (
        <Modal
            isOpen={isOpen}
            onAfterOpen={afterOpenModal}
            onRequestClose={onCloseModal}
            style={customStyles}
            // className="Modal"
            overlayClassName="Overlay"
            contentLabel={lnT('common:exampleModal')}
        >
            <ModalSurface>
                <ModalHeader>
                    <HeaderIconButton
                        type="button"
                        aria-label={lnT('folders:closeFolderPicker')}
                        onClick={onCloseModal}
                    >
                        <IoMdClose size={20} />
                    </HeaderIconButton>
                    <ModalTitle>{title}</ModalTitle>
                    {renderMoveToInbox() || <HeaderSpacer aria-hidden="true" />}
                </ModalHeader>
                {renderMoveToRoot() ? (
                    <UtilityActions>
                        {renderMoveToRoot()}
                    </UtilityActions>
                ) : null}
                <SearchField>
                    <FiSearch size={17} />
                    <SearchInput
                        ref={inputRef}
                        autoFocus
                        onKeyDown={e => {
                            if (e.keyCode === 40 || e.keyCode === 38) {
                                e.preventDefault();
                            }
                        }}
                        value={folderSearchInput}
                        onChange={e => handleFolderSearchChange(e.target.value)}
                        type="text"
                        placeholder={lnT('messages:searchFolders')}
                    />
                </SearchField>

                <FoldersViewport>
                    {loadContent()}
                </FoldersViewport>
                {/* {loadMoreCheck ? <LoadMore onClick={() => loadMore()}>Load more</LoadMore> : null} */}
                <KeyboardHint>{lnT('folders:useAndEnterToSelect')}</KeyboardHint>
            </ModalSurface>
        </Modal>
    );
}

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        color: 'white',
        borderRadius: 12,
        border: 0,
        width: 'min(92vw, 640px)',
        minWidth: 0,
        maxHeight: 'calc(100vh - 32px)',
        boxSizing: 'border-box',
        padding: 0,
        overflow: 'hidden',
        backgroundColor: '#222729',
        boxShadow: '0 18px 50px rgba(0, 0, 0, 0.34)'
    }
};

const ModalSurface = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    color: ${WHITE};
    border-radius: 12px;
`;

const ModalHeader = styled.div`
    min-height: 52px;
    padding: 8px 12px 4px;
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
`;

const HeaderIconButton = styled.button`
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: rgba(255, 255, 255, 0.72);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: ${WHITE};
    }
`;

const HeaderSpacer = styled.div`
    width: 32px;
    height: 32px;
`;

const ModalTitle = styled.div`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
    color: rgba(255, 255, 255, 0.86);
    font-size: 15px;
    font-weight: 600;
`;

const UtilityActions = styled.div`
    min-height: 32px;
    padding: 0 12px 6px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const SearchField = styled.div`
    min-height: 44px;
    margin: 4px 12px 8px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    gap: 7px;
    gap: 9px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 12px;
    background: transparent;
    color: rgba(255, 255, 255, 0.46);

    &:focus-within {
        border-color: rgba(255, 255, 255, 0.28);
        background: rgba(255, 255, 255, 0.025);
    }
`;

const SearchInput = styled.input`
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: ${WHITE};
    font-family: inherit;
    font-size: 14px;

    &::placeholder {
        color: rgba(255, 255, 255, 0.38);
    }
`;

const FoldersViewport = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 220px;
    height: min(440px, 55vh);
    max-height: min(440px, 55vh);
    padding: 0 12px 8px;
    overflow-y: auto;
`;

const KeyboardHint = styled.div`
    padding: 7px 12px 11px;
    text-align: center;
    color: rgba(255, 255, 255, 0.38);
    font-size: 11px;
`;

const LoadMore = styled.div`
    cursor: pointer;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 4px;
    margin-bottom: 4px;
    padding-top: 4px;
    padding-bottom: 4px;
    &:hover {
        color: rgba(255, 255, 255, 0.8);
    }
`;

const SearchFolder = styled.button`
    width: 100%;
    min-height: 44px;
    padding: 8px 14px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.025);
    color: rgba(255, 255, 255, 0.88);
    margin-top: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    &:hover {
        background-color: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.28);
    }
`;

const RootFolder = styled.button`
    min-height: 30px;
    padding: 6px 10px;
    border-radius: 9px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    font-family: inherit;
    font-size: 12px;
    line-height: 1;
    color: ${PALE_WHITE_6};
    &:hover {
        background-color: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.24);
        color: ${WHITE};
    }
`;

const LoadingText = styled.div`
    display: flex;
    // justify-content: center;
    color: ${PALE_WHITE_6};
    margin-top: 16px;
`;

export default SearchFoldersModal;
